import {ToolkitsActive} from '@/models/Config'

/**
 * Gets toolkits from localStorage
 * @returns {ToolkitsActive | null} Toolkits active configuration or null if not available
 */
export function getToolkitsFromLocalStorage (): ToolkitsActive | null {
  try {
    const appConfig = JSON.parse(localStorage.getItem('appConfig') || '{}')
    return appConfig?.org_settings?.[0]?.toolkits_active || null
  } catch (error) {
    console.warn('Failed to get toolkits from localStorage:', error)
    return null
  }
}

/**
 * Utility function to extract page key from route name, just in case of weird names
 */
export function getPageKeyFromRoute (route: string): string {
  if (route === 'reset-me') {
    return route
  }
  // Remove module prefix and page suffix
  const parts = route.split('-')
  if (parts.length < 2) {
    return route
  }

  // Handle special cases
  if (route.includes('home-page')) {
    return 'home'
  }
  if (route.includes('settings-')) {
    return 'settings'
  }
  if (route.includes('manage-main')) {
    return 'manage'
  }
  if (route.includes('markdown-builder')) {
    return 'builder'
  }

  // Extract page name from route
  const pagePart = parts.slice(1).join('-')
  return pagePart.replace('-page', '').replace('-main', '')
}

/**
 * Checks if a user is a superuser
 * @returns {boolean} True if the user is a superuser
 */
export function isSuperuser (): boolean {
  return localStorage.getItem('superuser') === 'true'
}

/**
 * Checks if a user has admin access to the settings module
 * @returns {boolean} True if the user's settings access level is admin
 */
export function isSettingsAdmin (): boolean {
  return localStorage.getItem('user_access_settings') === 'admin'
}

export type AnalyticsLimitKey = 'dashboards' | 'automations' | 'integrations'

/**
 * Reads the max item count for an analytics feature from `appConfig.org_settings[0].analytics`.
 * The `analytics` setting can be an object or a single-item array of that object.
 * @returns {number | null} null when no numeric limit is set (unlimited)
 */
export function getAnalyticsLimit (key: AnalyticsLimitKey): number | null {
  try {
    const appConfig = JSON.parse(localStorage.getItem('appConfig') || '{}')
    const analytics = appConfig?.org_settings?.[0]?.toolkits_active.analytics
    const limits = Array.isArray(analytics) ? analytics[0] : analytics
    const value = limits?.[key]
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  } catch (error) {
    console.warn('Failed to get analytics limits from localStorage:', error)
    return null
  }
}

/**
 * Computes usage-limit status for an analytics feature (personal dashboards, automations, integrations).
 * Superusers always bypass the limit.
 * @param {AnalyticsLimitKey} key - Feature key in the org `analytics` setting
 * @param {number} count - Current number of items the user already has
 * @returns {{reached: boolean, limit: number | null}} limit is null when unlimited
 */
export function getAnalyticsLimitStatus (key: AnalyticsLimitKey, count: number): { reached: boolean, limit: number | null } {
  if (isSuperuser()) {
    return { reached: false, limit: null }
  }
  const limit = getAnalyticsLimit(key)
  if (limit === null) {
    return { reached: false, limit: null }
  }
  return { reached: count >= limit, limit }
}

/**
 * Checks if a page is always accessible regardless of toolkits
 * @param {string} module - The module name
 * @param {string} page - The page name
 * @returns {boolean} True if the page is always accessible
 */
export function isAlwaysAccessiblePage (module: string, page: string): boolean {
  const alwaysAccessiblePages = ['manage', 'analytics', 'settings', 'reset-me']
  if (alwaysAccessiblePages.includes(page)) {
    return true
  }

  // Additional always accessible page for stock module
  return module === 'stock' && page === 'option'
}
